import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi, UpdateProfileData } from '@/lib/auth/api';
import { useAuthStore } from '@/lib/auth/store';
import { toast } from 'sonner';

export interface ProfileFormData {
  name: string;
  email: string;
  password: string;
}

export function useProfile() {
  const { t } = useTranslation();
  const { user, setUser, clearAuth } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const profileData = await authApi.getProfile();
      setUser(profileData);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('me.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const getFormData = useCallback((): ProfileFormData => {
    return {
      name: user?.name || '',
      email: user?.email || '',
      password: '',
    };
  }, [user]);

  const saveProfile = useCallback(
    async (formData: ProfileFormData) => {
      try {
        setIsSaving(true);

        const updateData: UpdateProfileData = {};

        if (formData.name !== user?.name) {
          updateData.name = formData.name;
        }

        if (formData.email !== user?.email) {
          updateData.email = formData.email;
        }

        if (formData.password) {
          updateData.password = formData.password;
        }

        if (Object.keys(updateData).length === 0) {
          toast.info(t('me.noChanges'));
          return true;
        }

        const updatedUser = await authApi.updateProfile(updateData);
        setUser(updatedUser);
        toast.success(t('me.saveSuccess'));

        if (updateData.password) {
          toast.info(t('me.passwordChangedLogout'));
          setTimeout(() => {
            clearAuth();
          }, 2000);
        }

        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('me.saveError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t, user, setUser, clearAuth]
  );

  const deleteAccount = useCallback(async () => {
    try {
      setIsDeleting(true);
      await authApi.deleteAccount();
      toast.success(t('me.deleteSuccess'));
      clearAuth();
      return true;
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('me.deleteError'));
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [t, clearAuth]);

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    } else {
      fetchProfile();
    }
  }, [fetchProfile, user]);

  return {
    user,
    isLoading,
    isSaving,
    isDeleting,
    getFormData,
    saveProfile,
    deleteAccount,
    refetch: fetchProfile,
  };
}
